import { useI18n } from "@core/i18n/i18n";
import { sendAnalyticsEventStartInvite } from "@domain/healthcare-team/healthcare-team-analytics";
import { Patient } from "@domain/patient/patient";
import { useUser } from "@domain/user/hooks/use-user";
import { User } from "@domain/user/user";
import styled from "@emotion/styled";
import { ContentWithTooltip } from "@ui/components/common/content-with-tooltip";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { Error404, NetworkError } from "@ui/components/common/error";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { HealthcareTeamMemberRow } from "@ui/pages/patient-details/components/healthcare-team-tab/components/healthcare-team-member-row";
import { useHealthcareTeam } from "@ui/pages/patient-details/hooks/use-healthcare-team";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint, row, stack } from "@ui/style";
import { isNil } from "@utils/assert";

import { Button } from "primereact/button";
import React, { useCallback, useContext } from "react";

interface HealthcareTeamTabProps {
	patient: Patient;
}

export const HealthcareTeamTab: React.FC<HealthcareTeamTabProps> = ({ patient }): JSX.Element => {
	const { error: isHealthcareTeamError, healthcareTeam, refreshHealthcareTeam } = useHealthcareTeam(patient.id);
	const { format } = useI18n();
	const { isMobile } = useDimensions();
	const dialogContext = useContext(DialogContext);
	const user = useUser();

	const openInviteDialog = useCallback(
		(currentPatient: Patient, currentUser: User) => {
			dialogContext?.openModal(ModalId.HealthcareTeamInvite, {
				patient: currentPatient,
				invitingPt: currentUser,
			});
			sendAnalyticsEventStartInvite(currentPatient);
		},
		[dialogContext],
	);

	if (isHealthcareTeamError) {
		return <NetworkError onRefresh={refreshHealthcareTeam} />;
	}

	if (isNil(user)) {
		return <Error404 />;
	}

	return (
		<>
			<TabHeaderContainer>
				<p className={isMobile ? "p-size-l-bold" : "p-header-3"}>{format("patientDetails.tabs.healthcareTeam")}</p>
				<Button
					label={format("patientDetails.healthcareTeam.inviteColleagueButton")}
					onClick={() => openInviteDialog(patient, user)}
				/>
			</TabHeaderContainer>

			<Container>
				<TooltipContainer>
					<ContentWithTooltip
						tooltip={format("patientDetails.healthcareTeam.teamMemberTooltip")}
						text={format("patientDetails.healthcareTeam.teamMembersTitle", {
							count: healthcareTeam?.healthcareTeamMembers.length ?? "-",
						})}
					/>
				</TooltipContainer>

				{healthcareTeam?.healthcareTeamMembers.map(member => (
					<HealthcareTeamMemberRow key={member.id} member={member} patient={patient} user={user} />
				))}
			</Container>
		</>
	);
};

const TabHeaderContainer = styled.div`
	${row(undefined, "spacebetween", "center")};
	@media ${physioMeMobileBreakpoint} {
		${stack(24)};
		margin-top: 8px;
		padding-bottom: 24px;
	}
`;

const Container = styled.div`
	margin-top: 16px;
	@media ${physioMeTabletBreakpoint} {
		margin-left: -32px;
		margin-right: -32px;
	}
	@media ${physioMeMobileBreakpoint} {
		margin-left: -16px;
		margin-right: -16px;
	}
`;

const TooltipContainer = styled.div`
	@media ${physioMeTabletBreakpoint} {
		margin-left: 24px;
		margin-bottom: 16px;
	}
`;
